<script setup>
/**
 * SzStatusSegmented — pill com 3 botões "Abertas | Fechadas | Todas".
 *
 * Reflete o estado de `chatStatusFilter` (open/resolved/all). Os status
 * `pending` e `snoozed` continuam disponíveis no SelectMenu do dropdown
 * de sort; se ativo um deles, nenhum botão fica selecionado aqui.
 */
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  /** Status atualmente ativo (vem do ref local de ChatList). Se ausente,
   *  cai no getter do Vuex como fallback. */
  activeStatus: { type: String, default: '' },
});
const emit = defineEmits(['changeStatus']);
const store = useStore();
const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const ITEMS = [
  { value: 'open',     label: 'Abertas'  },
  { value: 'resolved', label: 'Fechadas' },
  { value: 'all',      label: 'Todas'    },
];

const currentActive = computed(
  () =>
    props.activeStatus ||
    chatStatusFilter.value ||
    wootConstants.STATUS_TYPE.OPEN
);

function pickStatus(value) {
  if (value === currentActive.value) return;
  // Emite no padrão do ConversationBasicFilter para que ChatListHeader
  // propague via basicFilterChange → ChatList.onBasicFilterChange.
  // Isso atualiza o activeStatus ref local de ChatList e refaz o fetch.
  emit('changeStatus', value);
  store.dispatch('setChatStatusFilter', value);
  updateUISettings({
    conversations_filter_by: {
      status: value,
      order_by:
        chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC,
    },
  });
}
</script>

<template>
  <div class="sz-stseg" role="tablist" aria-label="Filtro de status">
    <div
      v-for="it in ITEMS"
      :key="it.value"
      role="tab"
      tabindex="0"
      :aria-selected="currentActive === it.value"
      class="sz-stseg-item"
      :class="{ 'sz-stseg-item--on': currentActive === it.value }"
      @click="pickStatus(it.value)"
      @keydown.enter.prevent="pickStatus(it.value)"
      @keydown.space.prevent="pickStatus(it.value)"
    ><span class="sz-stseg-item-text">{{ it.label }}</span></div>
  </div>
</template>

<style scoped>
/* Container: pill cinza */
.sz-stseg {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  padding: 4px;
  background: #F1F1F1;
  border-radius: 8px;
  font-family: 'Inter', system-ui, sans-serif;
  flex-shrink: 0;
  box-sizing: border-box;
}

/* Responsivo: container estreito reduz padding/font dos items pra liberar
   espaço pros ícones à direita (filtro, sort, switch layout).
   Threshold 420px cobre os 2 modos do ChatList:
   - w-[340px] (default <2xl) → 340 ≤ 420, ativa
   - w-[412px] (2xl) → 412 ≤ 420, ativa também (margin de 8px) */
@container szchlh (max-width: 420px) {
  .sz-stseg { padding: 3px; gap: 1px; }
  .sz-stseg-item { padding: 0 6px; }
  .sz-stseg-item-text { font-size: 11px; }
}
@container szchlh (max-width: 340px) {
  .sz-stseg-item { padding: 0 4px; }
  .sz-stseg-item-text { font-size: 10px; }
}
/* Item — DIV (não button) pra escapar de qualquer reset global de button */
.sz-stseg-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  user-select: none;
  box-sizing: border-box;
}
.sz-stseg-item-text {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: #888888;
}
.sz-stseg-item:hover:not(.sz-stseg-item--on) .sz-stseg-item-text {
  color: #3F3F3F;
}
.sz-stseg-item--on {
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.sz-stseg-item--on .sz-stseg-item-text {
  color: #3F3F3F;
}
.sz-stseg-item:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}

/* Dark mode (cores do Figma) */
.dark .sz-stseg { background: #26282B; }
.dark .sz-stseg-item-text { color: #B0B0B0; }
.dark .sz-stseg-item:hover:not(.sz-stseg-item--on) .sz-stseg-item-text {
  color: #E5E7EB;
}
.dark .sz-stseg-item--on {
  background: #17181B;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}
.dark .sz-stseg-item--on .sz-stseg-item-text { color: #FFFFFF; }
</style>
